import { ApiService } from './api';

export interface Department {
  id: number;
  name: string;
  description: string;
  color: string;
  is_active: boolean;
  settings?: any;
  stats?: DepartmentStats;
  created_at: string;
  updated_at: string;
}

export interface DepartmentStats {
  teams_count: number;
  active_teams_count: number;
  members_count: number;
}

export interface CreateDepartmentData {
  name: string;
  description?: string;
  color?: string;
  is_active?: boolean;
  settings?: any;
}

export interface UpdateDepartmentData {
  name?: string;
  description?: string;
  color?: string;
  is_active?: boolean;
  settings?: any;
}

class DepartmentService {
  /**
   * Get all departments with pagination
   */
  static async getDepartments(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    active?: boolean;
  }): Promise<{
    departments: Department[];
    total: number;
    current_page: number;
    last_page: number;
    meta?: any;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          data: Department[];
          total: number;
          current_page: number;
          last_page: number;
        };
        meta?: any;
      }>('/admin/departments', { params });
      
      return {
        departments: response.data.data,
        total: response.data.total,
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        meta: response.meta
      };
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw new Error('Failed to load departments');
    }
  }

  /**
   * Get a specific department by ID
   */
  static async getDepartment(id: number): Promise<Department> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: Department;
      }>(`/admin/departments/${id}`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching department:', error);
      throw new Error('Failed to load department');
    }
  }

  /**
   * Create a new department
   */
  static async createDepartment(data: CreateDepartmentData): Promise<Department> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        data: Department;
        message: string;
      }>('/admin/departments', data);
      
      return response.data;
    } catch (error) {
      console.error('Error creating department:', error);
      throw new Error('Failed to create department');
    }
  }

  /**
   * Update an existing department
   */
  static async updateDepartment(id: number, data: UpdateDepartmentData): Promise<Department> {
    try {
      const response = await ApiService.put<{
        success: boolean;
        data: Department;
        message: string;
      }>(`/admin/departments/${id}`, data);
      
      return response.data;
    } catch (error) {
      console.error('Error updating department:', error);
      throw new Error('Failed to update department');
    }
  }

  /**
   * Delete a department
   */
  static async deleteDepartment(id: number): Promise<void> {
    try {
      await ApiService.delete<{
        success: boolean;
        message: string;
      }>(`/admin/departments/${id}`);
    } catch (error) {
      console.error('Error deleting department:', error);
      throw new Error('Failed to delete department');
    }
  }

  /**
   * Get simple list of departments for dropdowns
   */
  static async getDepartmentsList(): Promise<Department[]> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        departments: Department[];
      }>('/admin/departments/list');
      
      return response.departments;
    } catch (error) {
      console.error('Error fetching departments list:', error);
      throw new Error('Failed to load departments list');
    }
  }

  /**
   * Get department statistics
   */
  static async getDepartmentStats(): Promise<{
    total_departments: number;
    active_departments: number;
    department_details: Array<{
      id: number;
      name: string;
      color: string;
      stats: DepartmentStats;
    }>;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          total_departments: number;
          active_departments: number;
          department_details: Array<{
            id: number;
            name: string;
            color: string;
            stats: DepartmentStats;
          }>;
        };
      }>('/admin/departments/stats');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching department stats:', error);
      throw new Error('Failed to load department statistics');
    }
  }
}

export default DepartmentService;